import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';

import { useConference } from '../conferenceContext';
import { captureException } from 'shared/utils/handlerErrors';
import { CaptionData } from 'features/video-conference/constants/types';
import { createCaptionData } from 'features/video-conference/utils';

interface CaptionProviderProps {
  children: React.ReactNode;
}

type CaptionContextType = {
  remoteTranscript: string;
  setRemoteTranscript: (value: string) => void;
  isCaptionActive: boolean;
  setIsCaptionActive: (value: boolean) => void;
  startListeningCaptions: () => Promise<void>;
  stopListeningCaptions: () => void;
  isRemoteCaptionActive: boolean;
  setIsRemoteCaptionActive: (value: boolean) => void;
  sendCaptionMessageToParticipant: (value: CaptionData) => void;
};

export const CaptionContext = createContext<CaptionContextType>(null!);

export const CaptionProvider = ({ children }: CaptionProviderProps) => {
  const [remoteTranscript, setRemoteTranscript] = useState<string>('');
  const [isCaptionActive, setIsCaptionActive] = useState<boolean>(false);
  const [isRemoteCaptionActive, setIsRemoteCaptionActive] =
    useState<boolean>(false);

  const {
    browserSupportsSpeechRecognition,
    transcript,
    listening,
    resetTranscript,
  } = useSpeechRecognition();
  const { room, isMicrophoneEnable } = useConference();

  const startListeningCaptions = useCallback(async () => {
    if (!browserSupportsSpeechRecognition) return;

    await SpeechRecognition.startListening({
      language: 'pt-BR',
      continuous: true,
    });
  }, [browserSupportsSpeechRecognition]);

  const stopListeningCaptions = useCallback(() => {
    if (!browserSupportsSpeechRecognition) return;

    SpeechRecognition.stopListening();
    resetTranscript();
  }, [browserSupportsSpeechRecognition, resetTranscript]);

  const sendCaptionMessageToParticipant = useCallback(
    (data: CaptionData) => {
      if (!room) return;

      const [localDataTrackPublication] = Array.from(
        room.localParticipant.dataTracks.values(),
      );

      localDataTrackPublication?.track?.send(JSON.stringify(data));
    },
    [room],
  );

  useEffect(() => {
    const handleCaptions = async () => {
      if (isRemoteCaptionActive && isMicrophoneEnable) {
        await startListeningCaptions();
        return;
      }

      if (listening) stopListeningCaptions();
    };

    handleCaptions().catch(captureException);
  }, [
    isMicrophoneEnable,
    isRemoteCaptionActive,
    listening,
    startListeningCaptions,
    stopListeningCaptions,
  ]);

  useEffect(() => {
    if (!listening) return;

    const captionData = createCaptionData({
      captionType: 'caption-transcript',
      isCaptionActive: true,
      transcript,
    });

    sendCaptionMessageToParticipant(captionData);
  }, [listening, sendCaptionMessageToParticipant, transcript]);

  useEffect(() => {
    if (!isCaptionActive) setRemoteTranscript('');
  }, [isCaptionActive]);

  const contextValue = useMemo(
    () => ({
      remoteTranscript,
      setRemoteTranscript,
      isCaptionActive,
      setIsCaptionActive,
      isRemoteCaptionActive,
      setIsRemoteCaptionActive,
      startListeningCaptions,
      stopListeningCaptions,
      sendCaptionMessageToParticipant,
    }),
    [
      remoteTranscript,
      isCaptionActive,
      isRemoteCaptionActive,
      startListeningCaptions,
      stopListeningCaptions,
      sendCaptionMessageToParticipant,
    ],
  ) as CaptionContextType;

  return (
    <CaptionContext.Provider value={contextValue}>
      {children}
    </CaptionContext.Provider>
  );
};

export function useCaptionContext(): CaptionContextType {
  const context = useContext(CaptionContext);

  if (!context) {
    throw new Error('useCaptionContext must be used within an CaptionProvider');
  }
  return context;
}
